import { LogOut, Shield, ChevronDown, Settings, CreditCard, LayoutDashboard, DownloadCloud } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DownloadsPanel } from './DownloadsPanel';
import { Button } from '@/components/ui/button';

interface UserMenuProps {
  onOpenSettings: () => void;
}

export function UserMenu({ onOpenSettings }: UserMenuProps) {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login');
  };

  const getInitial = () => {
    if (profile?.full_name) {
      return profile.full_name.charAt(0).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <div className="relative" ref={menuRef}>
      <div className="flex items-center gap-1 w-full">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-3 flex-1 p-2 rounded-lg hover:bg-sidebar-accent transition-colors"
        >
          <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
            {getInitial()}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium text-sidebar-foreground truncate">
              {profile?.full_name || user?.email}
            </p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        
        <Popover>
          <PopoverTrigger asChild>
            <Button 
                variant="outline" 
                size="icon" 
                className="h-10 w-10 rounded-lg shrink-0 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800" 
                title="Mis Descargas"
            >
              <DownloadCloud className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="right" align="end" className="w-[360px] p-0 shadow-2xl border-violet-100">
            <div className="p-3 border-b flex items-center gap-2 bg-violet-50/50">
              <DownloadCloud className="h-4 w-4 text-violet-700" />
              <span className="text-sm font-semibold text-violet-900">Mis Descargas</span>
            </div>
            <div className="p-0">
               <DownloadsPanel mode="funcionarios" />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
          {/* Admin Dashboard button for super_admin */}
          {profile?.role === 'super_admin' && (
            <button
              onClick={() => {
                navigate('/admin/dashboard');
                setIsOpen(false);
              }}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-accent transition-colors text-left"
            >
              <LayoutDashboard className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Panel Admin</span>
            </button>
          )}
          <button
            onClick={() => {
              onOpenSettings();
              setIsOpen(false);
            }}
            className="flex items-center gap-3 w-full px-4 py-3 hover:bg-accent transition-colors text-left"
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Configuración</span>
          </button>
          <button
            onClick={() => {
              navigate('/app/subscription');
              setIsOpen(false);
            }}
            className="flex items-center gap-3 w-full px-4 py-3 hover:bg-accent transition-colors text-left"
          >
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Mi suscripción</span>
          </button>
          <button
            onClick={() => {
              navigate('/app/security');
              setIsOpen(false);
            }}
            className="flex items-center gap-3 w-full px-4 py-3 hover:bg-accent transition-colors text-left"
          >
            <Shield className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Seguridad</span>
          </button>
          <div className="h-px bg-border" />
          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 w-full px-4 py-3 hover:bg-accent transition-colors text-left text-destructive"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm">Cerrar sesión</span>
          </button>
        </div>
      )}
    </div>
  );
}
